import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppNotification } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { PageHero } from '@/components/PagePlaceholder';
import { Bell, CalendarClock, Clapperboard, Sparkles } from 'lucide-react';
import { inboxApi } from '@/features/notifications/api';
import { VIEWER_QUERY_KEY, useAuth } from '@/features/auth/AuthContext';

const ICONS = { show: Clapperboard, premiere: CalendarClock, recommendation: Sparkles, system: Bell } as const;

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

function Item({ n }: { n: AppNotification }) {
  const Icon = ICONS[n.kind] ?? Bell;
  const inner = (
    <div className={cn('card flex gap-3 p-4', !n.read && 'border-brand/40 bg-brand/5')}>
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-raised text-brand-soft">
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-ink">{n.title}</p>
          {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-brand" aria-label="unread" />}
        </div>
        {n.body && <p className="mt-0.5 text-sm text-ink-muted">{n.body}</p>}
        <p className="mt-1 text-xs text-ink-faint">{timeAgo(n.createdAt)}</p>
      </div>
    </div>
  );
  return n.link ? (
    <Link to={n.link} className="block">{inner}</Link>
  ) : (
    inner
  );
}

export function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['inbox'], queryFn: inboxApi.list });

  const markAll = useMutation({
    mutationFn: inboxApi.markAllRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inbox'] });
      void qc.invalidateQueries({ queryKey: VIEWER_QUERY_KEY });
    },
  });

  // Mark everything read once the inbox is opened and there's something unread.
  useEffect(() => {
    if (data && data.unread > 0) markAll.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.unread]);

  if (!user) {
    navigate('/login');
    return null;
  }

  const list = data?.notifications ?? [];

  return (
    <div>
      <PageHero eyebrow="Notifications" title="Your notifications" />
      <div className="container-page pb-16">
        <div className="mx-auto max-w-2xl space-y-2.5">
          {isLoading ? (
            <p className="text-sm text-ink-faint">Loading…</p>
          ) : list.length === 0 ? (
            <div className="card px-6 py-14 text-center">
              <Bell size={28} className="mx-auto text-ink-faint" strokeWidth={1.5} />
              <p className="mt-3 font-display font-bold text-ink">You're all caught up</p>
              <p className="mt-1 text-sm text-ink-muted">New shows, premieres and picks for you will appear here.</p>
            </div>
          ) : (
            list.map((n) => <Item key={n.id} n={n} />)
          )}
        </div>
      </div>
    </div>
  );
}
